/**
 * Reusable input mask formatters for survey text inputs
 */

/**
 * Formats flexible US date input.
 * - Numeric keyboard on mobile
 * - Pure-digit fast entry auto-formats as MM/DD/YYYY (e.g. 04121992 -> 04/12/1992)
 * - Honors separators the user types (`/`, `-`, `.`) so single-digit month/day and
 *   2-digit years work too (e.g. 4/12/1992, 04/12/92). Parsing is finalized by
 *   convertDateToISODate, which accepts all these shapes.
 */
export const dateMaskMMDDYYYY = (inputElement: HTMLInputElement, question: any) => {
  inputElement.setAttribute('type', 'tel');
  inputElement.setAttribute('inputmode', 'numeric');
  inputElement.style.maxWidth = '100%';
  inputElement.style.width = '100%';
  inputElement.style.boxSizing = 'border-box';
  inputElement.style.minHeight = '52px';
  inputElement.style.padding = '15px 16px';

  inputElement.addEventListener('input', (event: any) => {
    // Normalize common separators to '/', keep only digits + slash, collapse dupes.
    const raw = event.target.value
      .replace(/[.\-\s]+/g, '/')
      .replace(/[^\d/]/g, '')
      .replace(/\/{2,}/g, '/');

    // Distribute digits across MM / DD / YYYY (caps 2 / 2 / 4) in a single pass.
    // A user-typed '/' advances to the next segment; digits that overflow a
    // segment's cap spill forward (auto-advance) into the next one. This unifies
    // pure-digit fast entry (08071992 -> 08/07/1992) with single-digit / 2-digit
    // year entry using the user's own separators (4/12/92) — and, crucially,
    // stops the day field from swallowing the first year digit once an
    // auto-inserted slash is present (the bug where 08/07 couldn't reach a year).
    const caps = [2, 2, 4];
    const segments = ['', '', ''];
    let seg = 0;
    for (let i = 0; i < raw.length && seg < segments.length; i++) {
      const char = raw[i];
      if (char === '/') {
        seg += 1;
        continue;
      }
      if (segments[seg].length >= caps[seg]) {
        seg += 1;
        if (seg >= segments.length) break;
      }
      segments[seg] += char;
    }

    // Render only the segments reached so far, preserving a trailing separator
    // the user just typed (e.g. "08/" stays "08/").
    let segmentCount = 1;
    if (segments[1]) segmentCount = 2;
    if (segments[2]) segmentCount = 3;
    if (raw.endsWith('/')) segmentCount = Math.min(segments.length, Math.max(segmentCount, seg + 1));
    const formattedValue = segments.slice(0, segmentCount).join('/');

    // Update the input value and question value
    event.target.value = formattedValue;
    question.value = formattedValue;
  });

  inputElement.addEventListener('keydown', (event: any) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      inputElement.blur(); // This will hide the keyboard
    }
  });
};

/**
 * Formats phone number input as (XXX) XXX-XXXX
 * - Shows numeric keyboard on mobile
 * - Auto-adds parentheses, space, and dash
 * - Limits to 10 digits
 */
export const phoneMaskUS = (inputElement: HTMLInputElement, question: any) => {
  inputElement.setAttribute('type', 'tel');
  inputElement.setAttribute('inputmode', 'numeric');
  inputElement.style.maxWidth = '100%';
  inputElement.style.width = '100%';
  inputElement.style.boxSizing = 'border-box';
  inputElement.style.minHeight = '52px';
  inputElement.style.padding = '15px 16px';

  inputElement.addEventListener('input', (event: any) => {
    let value = event.target.value.replace(/\D/g, ''); // Remove non-numeric characters

    // Limit to 10 digits
    if (value.length > 10) {
      value = value.substring(0, 10);
    }

    // Format as (XXX) XXX-XXXX
    let formattedValue = '';
    for (let i = 0; i < value.length; i++) {
      if (i === 0) {
        formattedValue += '(';
      }
      if (i === 3) {
        formattedValue += ') ';
      }
      if (i === 6) {
        formattedValue += '-';
      }
      formattedValue += value[i];
    }

    // Update the input value and question value
    event.target.value = formattedValue;
    question.value = formattedValue;
  });

  inputElement.addEventListener('keydown', (event: any) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      inputElement.blur(); // This will hide the keyboard
    }
  });
};

export const wholeNumberMask = (inputElement: HTMLInputElement, question: any) => {
  inputElement.setAttribute('type', 'tel');
  inputElement.setAttribute('inputmode', 'numeric');
  inputElement.setAttribute('pattern', '[0-9]*');
  inputElement.style.maxWidth = '100%';
  inputElement.style.width = '100%';
  inputElement.style.boxSizing = 'border-box';
  inputElement.style.minHeight = '52px';
  inputElement.style.padding = '15px 16px';

  inputElement.addEventListener('input', (event: any) => {
    const value = event.target.value.replace(/\D/g, '');
    event.target.value = value;
    question.value = value;
  });

  inputElement.addEventListener('keydown', (event: any) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      inputElement.blur();
    }
  });
};
